import { EmotionJSX } from "@emotion/react/types/jsx-namespace";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";

interface AppHeaderContextProps {
  nav: EmotionJSX.Element | undefined;
  setNav: (content: EmotionJSX.Element) => void;
  content: EmotionJSX.Element | undefined;
  setContent: (content: EmotionJSX.Element) => void;
  guides: EmotionJSX.Element | undefined;
  setGuides: (guides: EmotionJSX.Element) => void;
  reset: () => void;
}

const AppHeaderContext = createContext<AppHeaderContextProps | undefined>(
  undefined,
);

/*
 * Context for setting the app header content
 */
export const AppHeaderProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [nav, setNav] = useState<EmotionJSX.Element>();
  const [content, setContent] = useState<EmotionJSX.Element>();
  const [guides, setGuides] = useState<EmotionJSX.Element>();
  const reset = useCallback(() => {
    setContent(undefined);
    setNav(undefined);
    setGuides(undefined);
  }, []);

  return (
    <AppHeaderContext.Provider
      value={{ nav, setNav, content, setContent, guides, setGuides, reset }}
    >
      {children}
    </AppHeaderContext.Provider>
  );
};

export const useAppHeader = (): AppHeaderContextProps => {
  const context = useContext(AppHeaderContext);
  if (!context) {
    throw new Error("useAppHeader must be used within an AppHeaderProvider");
  }
  return context;
};

/*
 * Reusable hook for pages to set the app header content
 * @param header - the content to show in the header bar
 * @param nav - optional collapsible side <nav></nav>
 * @param guides - tours to show up next to the help menu
 */
export const useSetAppHeader = ({
  header,
  nav,
  guides,
}: {
  header: EmotionJSX.Element;
  nav?: EmotionJSX.Element;
  guides?: EmotionJSX.Element;
}): void => {
  const { setContent, setGuides, setNav, reset } = useAppHeader();

  React.useEffect(() => {
    setContent(header);
    if (nav) {
      setNav(nav);
    }
    if (guides) {
      setGuides(guides);
    }

    // resets the header when the component unmounts
    return () => {
      reset();
    };
  }, [setContent, setGuides, reset, setNav, header, nav, guides]);
};
